import { create } from 'zustand';

import type { WorkspaceView } from './workspace.js';

/**
 * "Follow assistant": while on, the editor shows what the AI assistant is
 * doing in this project — it switches to the editor for each file the
 * assistant reads, writes or renders and opens that file there. The choice
 * is remembered per project, and following only runs while this project's
 * tab is the visible one (hidden tabs pause and resume when shown again).
 */
export interface FollowState {
  projectId: number | null;
  following: boolean;
  /** This project's tab is the one on screen. */
  visible: boolean;
  /** Last thing followed, shown as a hint in the AI panel. */
  last: string | null;
  load: (projectId: number | null) => void;
  setFollowing: (on: boolean) => void;
  setVisible: (visible: boolean) => void;
  setLast: (text: string | null) => void;
}

const key = (projectId: number) => `prism.followAssistant.${projectId}`;

function readFollowing(projectId: number | null): boolean {
  if (projectId === null) return false;
  try {
    // Older builds kept one global Model-tab flag; start from it once.
    const stored = localStorage.getItem(key(projectId)) ?? localStorage.getItem('prism.model.followAgent');
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

export const useFollow = create<FollowState>((set, get) => ({
  projectId: null,
  following: false,
  visible: true,
  last: null,
  load: (projectId) => set({ projectId, following: readFollowing(projectId), last: null }),
  setFollowing: (following) => {
    set({ following });
    const pid = get().projectId;
    if (pid === null) return;
    try {
      localStorage.setItem(key(pid), String(following));
    } catch {
      // Storage unavailable: the choice lasts for this session.
    }
  },
  setVisible: (visible) => set({ visible }),
  setLast: (last) => set({ last }),
}));

/** Following is on and this project's tab is on screen. */
export function followActive(): boolean {
  const s = useFollow.getState();
  return s.following && s.visible;
}

/** Where a project file opens: the editor view for its kind. */
export function viewForPath(path: string): WorkspaceView {
  const p = path.toLowerCase();
  if (p.endsWith('.scene.json') || p.endsWith('.terrain.json') || p.endsWith('.prefab.json')) return 'scene';
  if (p.endsWith('.model.json') || p.endsWith('.glb') || p.endsWith('.gltf')) return 'model';
  if (p.endsWith('.pixel.json')) return 'pixel';
  if (p.endsWith('.sound.json')) return 'sound';
  if (p.endsWith('.video.json')) return 'video';
  return 'code';
}

/** Tool argument names that carry a project path (first match wins). */
const PATH_KEYS = ['path', 'file', 'model', 'scene', 'out', 'output', 'to', 'target', 'source', 'from'];

/** The project file an assistant tool call is about, if any. */
export function pathOfToolCall(tool: string, args: Record<string, unknown> | undefined): string | null {
  if (!args) return null;
  // move_file: show where it went.
  const keys = tool === 'move_file' ? ['to', ...PATH_KEYS] : PATH_KEYS;
  for (const k of keys) {
    const v = args[k];
    if (typeof v === 'string' && v.trim() && !/^[a-z]+:\/\//i.test(v)) return v.trim().replace(/\\/g, '/').replace(/^\.\//, '');
  }
  const paths = args.paths;
  if (Array.isArray(paths) && typeof paths[0] === 'string') return paths[0];
  return null;
}

/** Tools whose action is best shown on the task board. */
export const BOARD_TOOLS = new Set(['create_task', 'update_task', 'assign_task', 'read_board']);
/** Tools that do not point at anything worth showing. */
export const SKIP_TOOLS = new Set([
  'delete_file',
  'list_files',
  'find_files',
  'search_files',
  'memory_read',
  'memory_write',
  'memory_forget',
  'read_working_memory',
  'update_working_memory',
  'save_checkpoint',
  'web_search',
  'fetch_url',
  'prism_docs',
]);
